import type { MappedNotification } from 'common/header/notifications/Notifications.types';
import { getInvoiceNotificationTitle, isInvoiceNotification } from 'invoices/notifications/invoiceNotificationUtil';
import { beautifyName } from 'utils/textUtil';
import { getEntityType, getMetaData } from 'common/header/notifications/hooks/Notification.utils';
import { isNumber } from 'utils/guards';
import { MessageValue } from 'locale';
import { numberFormat } from 'utils/useNumberFormatter';
import { NotificationStatusModel, OcReportSourceTypeModel } from 'src/graphql/types';
import {
  NotificationFormatFunction,
  NotificationIntlKey
} from 'src/components/common/header/translations/useNotificationsIntl';
import { ExportSource } from 'src/utils/hooks/data/exports/utils/Exports.types';

type DescriptionIntlKeyPrefix = 'ExportEntityDesc' | 'ExportEntityDescExtended';

const getIntlBasedOnStatus = (
  format: NotificationFormatFunction,
  // biome-ignore lint/style/useDefaultParameterLast: to be fixed
  descIntlKeyPrefix: DescriptionIntlKeyPrefix = 'ExportEntityDesc',
  status: NotificationStatusModel | null | undefined,
  titleVariables: Record<string, MessageValue>,
  descriptionVariables: Record<string, MessageValue>
) => {
  if (status) {
    return {
      title: format(`ExportEntityTitle_${status}`, titleVariables),
      description: format(`${descIntlKeyPrefix}_${status}`, descriptionVariables)
    };
  }
  return null;
};

export const getExportTitle = (format: NotificationFormatFunction, notificationViewModel: MappedNotification) =>
  getIntlBasedOnStatus(
    format,
    'ExportEntityDesc',
    notificationViewModel.status,
    { prefix: getPrefix(notificationViewModel, format) },
    { totalCount: getTotalCount(notificationViewModel) }
  ) ?? {
    title: '',
    description: notificationViewModel.type === 'CSV_EXPORT' ? format('csv') : format('excel')
  };

export const getExportExtendedTitle = (
  format: NotificationFormatFunction,
  notificationViewModel: MappedNotification,
  locale: string
) => {
  const actualCount = getActualCount(notificationViewModel);

  const extendedWording =
    isNumber(actualCount) &&
    !!notificationViewModel &&
    getEntityType(notificationViewModel)?.toLocaleLowerCase() === 'timesheets';

  if (isInvoiceNotification(notificationViewModel)) {
    const title = getInvoiceNotificationTitle(notificationViewModel);
    if (!!title) {
      return {
        title,
        description: getExportTitle(format, notificationViewModel).description
      };
    }
  }

  if (!extendedWording) {
    return getExportTitle(format, notificationViewModel);
  }

  const totalCount = getTotalCount(notificationViewModel);
  const result = getIntlBasedOnStatus(
    format,
    'ExportEntityDescExtended',
    notificationViewModel.status,
    { prefix: getPrefix(notificationViewModel, format) },
    [
      NotificationStatusModel.Cancelled,
      NotificationStatusModel.Rejected,
      NotificationStatusModel.FinishedError
    ].includes(notificationViewModel.status ?? NotificationStatusModel.FinishedSuccess)
      ? { totalCount }
      : { totalCount, actualCount }
  );

  if (!result) {
    return {
      title: '',
      description: notificationViewModel.type === 'CSV_EXPORT' ? format('csv') : format('excel')
    };
  }

  return {
    ...result,
    description: `${result.description} ${getFeatureInfo(format, {
      notificationViewModel,
      featureKey: 'EXPORT_TIMESHEET_INCLUDE_EMPTY_ALLOCATION',
      visible: ![NotificationStatusModel.Cancelled].includes(
        notificationViewModel.status ?? NotificationStatusModel.FinishedSuccess
      ),
      variables: {
        count: numberFormat((totalCount || 0) - (actualCount || 0), { locale })
      }
    })}`.trimEnd()
  };
};

const getTotalCount = (notificationViewModel: MappedNotification) => {
  const countStr: string | undefined =
    getMetaData(notificationViewModel).find((e) => e?.key === 'COUNT')?.value ?? undefined;
  const hasCount = countStr !== undefined && countStr !== null;
  return hasCount ? parseInt(countStr, 10) : '';
};

const getActualCount = (notificationViewModel: MappedNotification) => {
  const countStr: string | undefined =
    getMetaData(notificationViewModel).find((e) => e?.key === 'ACTUAL_COUNT')?.value ?? undefined;
  const hasCount = countStr !== undefined && countStr !== null;
  return hasCount ? parseInt(countStr, 10) : null;
};

const getPrefix = (notificationViewModel: MappedNotification, format: NotificationFormatFunction) => {
  const metadata = getMetaData(notificationViewModel);
  const entityType = getEntityType(notificationViewModel);
  const sourceType = metadata.find((e) => e.key === 'SOURCE_TYPE')?.value;
  const name = metadata.find((e) => e.key === 'NAME')?.value;
  const entityName = entityType ? format(`${entityType}_single`) || beautifyName(entityType) : '';
  const isCustomObjectExportNotification = entityType && entityType === ExportSource.CustomObjects;

  if (!name) return entityName;
  if (isCustomObjectExportNotification) return name;

  switch (sourceType) {
    case OcReportSourceTypeModel.MyReport:
      return `${format('myReport')} ${name}`;
    case OcReportSourceTypeModel.Hub:
      return `${format('quickView')} ${name}`;
    case OcReportSourceTypeModel.StandardReport:
      return `${format('standardReport')} ${name}`;
  }

  return entityName;
};

interface Props {
  notificationViewModel: MappedNotification;
  featureKey: string;
  visible?: boolean;
  variables?: Record<string, MessageValue>;
}

const getFeatureInfo = (
  format: NotificationFormatFunction,
  { notificationViewModel, featureKey, visible, variables }: Props
): string => {
  const feature = getMetaData(notificationViewModel).find((it) => it.key === featureKey);
  if (!feature || !visible) {
    return '';
  }
  const message = format(
    `ExportEntityDescExtendedFeature_${feature.key}_${feature.value?.toString()}` as NotificationIntlKey,
    variables
  );
  return message ? `(${message})` : '';
};
